import { useMemo } from "react";
import currency from "currency.js";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { OptionTypeResponse } from "../../../codegen-api";
import MarketIcon from "./MarketIcon";
import {
  MarketCellContainer,
  MarketCellContent,
  Separator,
  SettlementInProgress,
  RowStatusCaption,
} from "./style";
import { nanosToSeconds } from "../../../utils/date";
import { getDisplayInstrumentName } from "../../../utils/displayName";

// PERPS and OPTION type
type IMarketCellProps = {
  instrumentName?: string;
  showSettlementInProgress?: boolean;
  // Tints the instrument name (e.g. green=long / red=short on the positions table).
  color?: string;
  // A close is in flight that could be this position, but could also be another
  // row on the same market. See IModifiedPosition.__tradeSyncSettling.
  settling?: boolean;
} & (
  | {
      // PERPS
      optionType?: undefined;
      expiry?: undefined;
      strike?: undefined;
      leverage: number;
    }
  | {
      // OPTIONS
      optionType?: OptionTypeResponse;
      // Nanos
      expiry?: string;
      strike?: string;
      leverage?: undefined;
    }
);

function MarketCell({
  instrumentName,
  optionType,
  expiry,
  strike,
  showSettlementInProgress,
  leverage,
  color,
  settling,
}: IMarketCellProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "PortfolioSettings.Table.MarketCell",
  });
  const displayInstrumentName =
    getDisplayInstrumentName(instrumentName) || instrumentName || "Unknown";

  const content = useMemo(() => {
    if (
      optionType !== undefined &&
      expiry !== undefined &&
      strike !== undefined
    ) {
      return (
        <div>
          <MarketCellContent>
            {t(optionType)}
            <Separator />
            {moment.unix(nanosToSeconds(expiry)).format("DD MMM YY")}
            <Separator />
            {currency(strike, { precision: 0 }).format()}
          </MarketCellContent>
          {showSettlementInProgress &&
            moment.unix(nanosToSeconds(expiry)).isSameOrBefore(moment()) && (
              <SettlementInProgress>
                {t("settlement_in_progress")}
              </SettlementInProgress>
            )}
        </div>
      );
    }
    return (
      <div>
        <MarketCellContent>
          <span style={color ? { color } : undefined}>
            {displayInstrumentName}
          </span>
          {leverage ? (
            <>
              <Separator />
              {leverage}x
            </>
          ) : null}
        </MarketCellContent>
        {settling && (
          // Deliberately "close pending", not "closing": when this shows, the
          // registry could NOT prove which of several rows on this market is the
          // one being closed, so claiming this row is on its way out would state
          // more than we know.
          <RowStatusCaption>
            {t("close_pending", { defaultValue: "Close pending" })}
          </RowStatusCaption>
        )}
      </div>
    );
  }, [
    settling,
    expiry,
    displayInstrumentName,
    leverage,
    optionType,
    showSettlementInProgress,
    strike,
    t,
    color,
  ]);

  return (
    <MarketCellContainer>
      <MarketIcon instrumentName={instrumentName} />
      {content}
    </MarketCellContainer>
  );
}

export default MarketCell;
